/**
 * A-to-B планировщик (референс RoutePlanner): произвольные старт и цель из
 * объектов площадки, режим пешком/авто/фура, расчёт по графу RVF (planRoute)
 * и линия на карте. Не меняет основной флоу (работает поверх ОБЗОРА).
 */
import { getLang, tr } from '@app/i18n'
import {
  localized,
  planRoute,
  type RoutePlan,
  type RouteProfile,
  resolveStart,
  type Venue,
} from '@app/rvf'
import { useMemo, useState } from 'preact/hooks'
import { a2bOpen, graph, langTick, venue, venueMap } from '../state'

interface PointItem {
  id: string
  name: string
  point: readonly [number, number]
}

function buildPoints(v: Venue): PointItem[] {
  const lang = getLang()
  const fb = v.venue.default_language
  const out: PointItem[] = []
  for (const p of v.pois) {
    out.push({ id: p.id, name: localized(p.name, lang, fb), point: p.point })
  }
  for (const u of v.units) {
    const ring = u.geometry.coordinates[0] ?? []
    if (ring.length === 0) continue
    let lon = 0
    let lat = 0
    for (const c of ring) {
      lon += c[0] ?? 0
      lat += c[1] ?? 0
    }
    out.push({
      id: u.id,
      name: localized(u.name, lang, fb),
      point: [lon / ring.length, lat / ring.length],
    })
  }
  return out
}

const PROFILES: readonly {
  id: RouteProfile
  key: 'travel.foot' | 'travel.vehicle' | 'travel.truck'
}[] = [
  { id: 'foot', key: 'travel.foot' },
  { id: 'vehicle', key: 'travel.vehicle' },
  { id: 'truck', key: 'travel.truck' },
]

export function A2bPlanner() {
  langTick.value
  const v = venue.value
  const g = graph.value
  const points = useMemo(() => (v ? buildPoints(v) : []), [v, langTick.value])
  const [startId, setStartId] = useState('')
  const [endId, setEndId] = useState('')
  const [profile, setProfile] = useState<RouteProfile>('foot')
  const [plan, setPlan] = useState<RoutePlan | null>(null)
  if (!v || !g) return null

  const close = (): void => {
    a2bOpen.value = false
    venueMap.value?.setRoute(null)
    setPlan(null)
  }

  const build = (): void => {
    const a = points.find((p) => p.id === startId) ?? points[0]
    const b = points.find((p) => p.id === endId)
    if (!a || !b || a.id === b.id) return
    const start = resolveStart(v, { geo: a.point })
    if (!start) return
    const next = planRoute(v, g, start, b.id, profile)
    setPlan(next)
    if (next) {
      venueMap.value?.setRoute(next)
      venueMap.value?.fitRoute(next)
    }
  }

  const minutes = plan ? Math.max(1, Math.round(plan.durationS / 60)) : 0

  return (
    <aside class="a2b glass anim-drawer">
      <div class="a2b-head">
        <div>
          <p class="tech-label a2b-kicker">{tr('a2b.kicker')}</p>
          <h2 class="a2b-title">{tr('a2b.title')}</h2>
        </div>
        <button type="button" class="icon-btn" aria-label={tr('common.close')} onClick={close}>
          ✕
        </button>
      </div>

      <div class="a2b-modes">
        {PROFILES.map((m) => (
          <button
            key={m.id}
            type="button"
            class={`a2b-mode${profile === m.id ? ' a2b-mode-on' : ''}`}
            onClick={() => setProfile(m.id)}
          >
            {tr(m.key)}
          </button>
        ))}
      </div>

      <label class="tech-label a2b-label a2b-label-a" htmlFor="a2b-from">
        {tr('a2b.from')}
      </label>
      <select
        id="a2b-from"
        class="a2b-select"
        value={startId}
        onChange={(e) => setStartId((e.target as HTMLSelectElement).value)}
      >
        {points.map((p) => (
          <option key={p.id} value={p.id}>
            {p.name}
          </option>
        ))}
      </select>

      <label class="tech-label a2b-label a2b-label-b" htmlFor="a2b-to">
        {tr('a2b.to')}
      </label>
      <select
        id="a2b-to"
        class="a2b-select"
        value={endId}
        onChange={(e) => setEndId((e.target as HTMLSelectElement).value)}
      >
        <option value="">{tr('a2b.pick')}</option>
        {points.map((p) => (
          <option key={p.id} value={p.id}>
            {p.name}
          </option>
        ))}
      </select>

      {plan && (
        <div class="a2b-summary">
          <div>
            <p class="tech-label a2b-sum-label">{tr('a2b.distance')}</p>
            <b>
              {Math.round(plan.distanceM)} {tr('unit.m')}
            </b>
          </div>
          <div class="a2b-sum-right">
            <p class="tech-label a2b-sum-label">{tr('a2b.time')}</p>
            <b>
              ~{minutes} {tr('unit.min')}
            </b>
          </div>
        </div>
      )}

      <button type="button" class="btn-primary a2b-build" onClick={build}>
        {tr('a2b.build')}
      </button>
    </aside>
  )
}
